// Admin Authentication UI Utilities
import { authAPI } from '../services';

// Get current session info from localStorage
export const getCurrentSession = () => {
  return JSON.parse(localStorage.getItem('admin-session') || '{}');
};

// Get available endpoints from localStorage
export const getStoredEndpoints = () => {
  try {
    const stored = localStorage.getItem('available-endpoints');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to get stored endpoints:', error);
    return [];
  }
};

// Get current selected endpoint
export const getCurrentEndpoint = () => {
  return localStorage.getItem('selected-endpoint') || null;
};

// Set current endpoint
export const setCurrentEndpoint = (endpoint) => {
  localStorage.setItem('selected-endpoint', endpoint);
  window.dispatchEvent(new CustomEvent('endpoint-changed', {
    detail: { endpoint }
  }));
};

// Check if user is authenticated
export const isAdminAuthenticated = () => {
  const token = localStorage.getItem('admin-token');
  const sessionInfo = getCurrentSession();
  
  if (!token) return false;
  
  // Check if token is expired
  if (sessionInfo.expiresAt && new Date() > new Date(sessionInfo.expiresAt)) {
    return false;
  }
  
  return true;
};

// Check if endpoints are available
export const hasAvailableEndpoints = () => {
  const endpoints = getStoredEndpoints();
  return endpoints.length > 0;
};

// Show IP error modal
export const showIpErrorModal = ({ currentIp, authorizedIps, sessionId, errorMessage }) => {
  // Remove existing modal if any
  const existingModal = document.querySelector('.ip-error-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const modal = document.createElement('div');
  modal.className = 'ip-error-modal';
  modal.innerHTML = `
    <div class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>🚨 Access Denied - IP Not Authorized</h3>
          <button class="modal-close" onclick="this.closest('.ip-error-modal').remove()">×</button>
        </div>
        
        <div class="modal-body">
          <div class="error-info">
            <p><strong>Error:</strong> ${errorMessage}</p>
            <p><strong>Your current IP:</strong> <code>${currentIp}</code></p>
            <p><strong>Authorized IPs:</strong></p>
            <ul class="authorized-ips-list">
              ${authorizedIps.map(ip => `<li><code>${ip}</code></li>`).join('')}
            </ul>
          </div>
          
          <div class="modal-actions">
            <button class="btn btn-primary" onclick="addCurrentIpAndRefresh('${currentIp}')">
              ✅ Authorize Current IP
            </button>
            <button class="btn btn-secondary" onclick="adminLogoutAndRedirect()">
              🔐 Logout and Re-login
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  // Add styles if not already present
  if (!document.getElementById('ip-error-modal-styles')) {
    const styles = document.createElement('style');
    styles.id = 'ip-error-modal-styles';
    styles.textContent = `
      .ip-error-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10000;
      }
      
      .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: 20px;
      }
      
      .modal-content {
        background: white;
        border-radius: 8px;
        max-width: 500px;
        width: 100%;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      }
      
      .modal-header {
        padding: 20px 20px 0;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }
      
      .modal-header h3 {
        margin: 0;
        color: #dc3545;
      }
      
      .modal-close {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;
      }
      
      .modal-body {
        padding: 20px;
      }
      
      .error-info {
        margin-bottom: 20px;
      }
      
      .authorized-ips-list {
        margin: 10px 0;
        padding-left: 20px;
      }
      
      .authorized-ips-list code {
        background: #f8f9fa;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: monospace;
      }
      
      .modal-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
      }
      
      .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s;
      }
      
      .btn-primary {
        background: #007bff;
        color: white;
      }
      
      .btn-primary:hover {
        background: #0056b3;
      }
      
      .btn-secondary {
        background: #6c757d;
        color: white;
      }
      
      .btn-secondary:hover {
        background: #545b62;
      }
    `;
    document.head.appendChild(styles);
  }

  document.body.appendChild(modal);
};

// Add current IP and refresh page
window.addCurrentIpAndRefresh = async (ip) => {
  try {
    const statusElement = document.querySelector('.modal-actions');
    statusElement.innerHTML = '<div style="text-align: center;">🔄 Authorizing IP...</div>';
    
    await authAPI.addCurrentIp(ip);
    
    // Close modal and refresh page
    document.querySelector('.ip-error-modal').remove();
    window.location.reload();
  } catch (error) {
    alert('Failed to authorize IP: ' + error.message);
    window.location.reload(); // Refresh anyway to reset state
  }
};

// Logout and redirect
window.adminLogoutAndRedirect = () => {
  authAPI.logout();
  window.location.href = '/admin/login';
};

// Render endpoint selector component
export const renderEndpointSelector = (containerId) => {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with ID "${containerId}" not found`);
    return;
  }

  const endpoints = getStoredEndpoints();
  const currentEndpoint = getCurrentEndpoint();

  if (endpoints.length === 0) {
    container.innerHTML = '<div class="no-endpoints">No endpoints available. Please authenticate first.</div>';
    return;
  }

  const selectorHtml = `
    <div class="endpoint-selector">
      <label for="endpoint-select">Select Endpoint:</label>
      <select id="endpoint-select" onchange="changeEndpoint(this.value)">
        ${endpoints.map(endpoint => `
          <option value="${endpoint.url}" ${endpoint.url === currentEndpoint ? 'selected' : ''}>
            ${endpoint.name || endpoint.url} (${endpoint.type || 'unknown'})
          </option>
        `).join('')}
      </select>
      <button onclick="refreshEndpointsFromSelector('${containerId}')" class="refresh-endpoints-btn">
        🔄 Refresh
      </button>
    </div>
  `;

  container.innerHTML = selectorHtml;

  // Add styles if not already present
  if (!document.getElementById('endpoint-selector-styles')) {
    const styles = document.createElement('style');
    styles.id = 'endpoint-selector-styles';
    styles.textContent = `
      .endpoint-selector {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 6px;
        border: 1px solid #dee2e6;
      }
      
      .endpoint-selector label {
        font-weight: bold;
        color: #495057;
      }
      
      .endpoint-selector select {
        flex: 1;
        padding: 6px 10px;
        border: 1px solid #ced4da;
        border-radius: 4px;
        background: white;
      }
      
      .refresh-endpoints-btn {
        background: #28a745;
        color: white;
        border: none;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
      }
      
      .refresh-endpoints-btn:hover {
        background: #218838;
      }
      
      .no-endpoints {
        text-align: center;
        color: #6c757d;
        padding: 20px;
        font-style: italic;
      }
    `;
    document.head.appendChild(styles);
  }
};

// Global functions for endpoint management
window.changeEndpoint = (endpoint) => {
  setCurrentEndpoint(endpoint);
  console.log('Endpoint changed to:', endpoint);
};

window.refreshEndpointsFromSelector = async (containerId) => {
  try {
    await authAPI.refreshEndpoints();
    renderEndpointSelector(containerId);
  } catch (error) {
    console.error('Failed to refresh endpoints:', error);
    alert('Failed to refresh endpoints: ' + error.message);
  }
};

// Render session information component
export const renderSessionInfo = async (containerId) => {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with ID "${containerId}" not found`);
    return;
  }

  try {
    container.innerHTML = '<div style="text-align: center;">🔄 Loading session info...</div>';
    
    const session = await authAPI.getSession();
    const endpoints = getStoredEndpoints();
    
    const sessionHtml = `
      <div class="admin-session-info">
        <div class="session-header">
          <h3>🛡️ Admin Session</h3>
          <button class="btn btn-sm btn-outline" onclick="refreshSessionInfo('${containerId}')">
            🔄 Refresh
          </button>
        </div>
        
        <div class="session-details">
          <div class="session-item">
            <label>Current IP:</label>
            <code>${session.currentIp}</code>
          </div>
          
          <div class="session-item">
            <label>Session ID:</label>
            <code>${session.sessionId}</code>
          </div>
          
          <div class="session-item">
            <label>Expires:</label>
            <span>${new Date(session.expiresAt).toLocaleString()}</span>
          </div>
          
          <div class="session-item">
            <label>Available Endpoints:</label>
            <span>${endpoints.length} endpoint(s)</span>
          </div>
        </div>
        
        <div class="authorized-ips-section">
          <h4>Authorized IPs:</h4>
          <ul class="authorized-ips">
            ${session.authorizedIps.map(ip => `
              <li class="ip-item">
                <code>${ip}</code>
                ${ip === session.currentIp ? 
                  '<span class="current-ip-badge">Current</span>' : 
                  `<button class="btn btn-sm btn-danger" onclick="removeIpAndRefresh('${ip}', '${containerId}')">❌</button>`
                }
              </li>
            `).join('')}
          </ul>
        </div>
        
        <div class="session-actions">
          <button class="btn btn-danger" onclick="confirmLogout()">🔐 Logout</button>
          <button class="btn btn-secondary" onclick="refreshEndpointsFromSession()">🔄 Refresh Endpoints</button>
        </div>
      </div>
    `;
    
    container.innerHTML = sessionHtml;
    
    // Add styles if not already present
    addSessionInfoStyles();
    
  } catch (error) {
    console.error('Failed to load session info:', error);
    container.innerHTML = `
      <div class="error-message">
        ❌ Failed to load session information: ${error.message}
        <button class="btn btn-sm" onclick="renderSessionInfo('${containerId}')">Retry</button>
      </div>
    `;
  }
};

// Add CSS styles for session info
const addSessionInfoStyles = () => {
  if (!document.getElementById('session-info-styles')) {
    const styles = document.createElement('style');
    styles.id = 'session-info-styles';
    styles.textContent = `
      .admin-session-info {
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 20px;
        background: #f8f9fa;
        max-width: 600px;
      }
      
      .session-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        border-bottom: 1px solid #ddd;
        padding-bottom: 10px;
      }
      
      .session-header h3 {
        margin: 0;
        color: #28a745;
      }
      
      .session-details {
        margin-bottom: 20px;
      }
      
      .session-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid #eee;
      }
      
      .session-item label {
        font-weight: bold;
        color: #495057;
      }
      
      .session-item code {
        background: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-family: monospace;
        border: 1px solid #ddd;
      }
      
      .authorized-ips-section h4 {
        margin: 15px 0 10px 0;
        color: #495057;
      }
      
      .authorized-ips {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      
      .ip-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        margin: 5px 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
      }
      
      .current-ip-badge {
        background: #28a745;
        color: white;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 12px;
      }
      
      .session-actions {
        margin-top: 20px;
        text-align: center;
        border-top: 1px solid #ddd;
        padding-top: 15px;
        display: flex;
        gap: 10px;
        justify-content: center;
      }
      
      .btn-sm {
        padding: 4px 8px;
        font-size: 12px;
      }
      
      .btn-outline {
        background: transparent;
        border: 1px solid #6c757d;
        color: #6c757d;
      }
      
      .btn-outline:hover {
        background: #6c757d;
        color: white;
      }
      
      .btn-danger {
        background: #dc3545;
        color: white;
      }
      
      .btn-danger:hover {
        background: #c82333;
      }
      
      .btn-secondary {
        background: #6c757d;
        color: white;
      }
      
      .btn-secondary:hover {
        background: #5a6268;
      }
      
      .error-message {
        color: #dc3545;
        text-align: center;
        padding: 20px;
      }
    `;
    document.head.appendChild(styles);
  }
};

// Global functions for UI interactions
window.refreshSessionInfo = (containerId) => {
  renderSessionInfo(containerId);
};

window.removeIpAndRefresh = async (ip, containerId) => {
  if (!window.confirm(`Are you sure you want to remove IP ${ip} from authorized list?`)) {
    return;
  }
  
  try {
    await authAPI.removeAuthorizedIp(ip);
    renderSessionInfo(containerId);
  } catch (error) {
    alert('Failed to remove IP: ' + error.message);
  }
};

window.confirmLogout = () => {
  if (window.confirm('Are you sure you want to logout?')) {
    authAPI.logout();
    window.location.href = '/admin/login';
  }
};

window.refreshEndpointsFromSession = async () => {
  try {
    const endpoints = await authAPI.refreshEndpoints();
    alert(`✅ Endpoints refreshed! Found ${endpoints.length} endpoint(s).`);
  } catch (error) {
    alert('Failed to refresh endpoints: ' + error.message);
  }
};

// Multi-service admin dashboard loader with endpoint discovery
export const loadAdminDashboard = async () => {
  try {
    // Check if we have valid authentication and endpoints
    if (!isAdminAuthenticated()) {
      throw new Error('Not authenticated');
    }
    
    if (!hasAvailableEndpoints()) {
      console.warn('No endpoints available, attempting to refresh...');
      await authAPI.refreshEndpoints();
    }
    
    // Load dashboard data from discovered endpoints
    const [authData] = await Promise.all([
      authAPI.getAllSessions().catch(e => ({ error: e.message }))
    ]);
    
    return {
      sessions: authData.error ? null : authData,
      endpoints: getStoredEndpoints(),
      currentEndpoint: getCurrentEndpoint(),
      errors: {
        sessions: authData.error
      }
    };
    
  } catch (error) {
    if (error.message.includes('IP not authorized')) {
      // IP error already handled by adminRequest
      return null;
    }
    console.error('Dashboard load failed:', error);
    throw error;
  }
};

// Initialize IP error handling and endpoint management
export const initializeIpErrorHandling = () => {
  // Listen for IP validation errors
  window.addEventListener('ip-validation-error', (event) => {
    console.log('IP validation error detected:', event.detail);
    showIpErrorModal(event.detail);
  });
  
  // Listen for admin logout events
  window.addEventListener('admin-logout', () => {
    console.log('Admin logged out');
    // You can add additional cleanup here
  });
  
  // Listen for session updates
  window.addEventListener('session-updated', (event) => {
    console.log('Session updated:', event.detail);
    // You can update UI components here
  });
  
  // Listen for endpoint changes
  window.addEventListener('endpoint-changed', (event) => {
    console.log('Endpoint changed:', event.detail);
    // You can update UI components here
  });
};

// Route protection utility
export const requireAdminAuth = () => {
  if (!isAdminAuthenticated()) {
    window.location.href = '/admin/login';
    return false;
  }
  return true;
};

// Admin login helper for encrypted key authentication
export const handleAdminLogin = async (encryptedKey) => {
  try {
    const result = await authAPI.login(encryptedKey);
    
    if (result.success) {
      console.log('Login successful:', result);
      
      // For encrypted key login, we'll have endpoints
      if (result.endpoints) {
        console.log(`Discovered ${result.endpoints.length} endpoints`);
      }
      
      return {
        success: true,
        sessionInfo: result.sessionInfo,
        endpoints: result.endpoints,
        message: 'Login successful! Redirecting...'
      };
    } else {
      return {
        success: false,
        error: result.error || 'Login failed'
      };
    }
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      error: error.message || 'Login failed'
    };
  }
};

// Create adminAuth object to export as default
const adminAuth = {
  getCurrentSession,
  getStoredEndpoints,
  getCurrentEndpoint,
  setCurrentEndpoint,
  isAdminAuthenticated,
  hasAvailableEndpoints,
  showIpErrorModal,
  renderEndpointSelector,
  renderSessionInfo,
  loadAdminDashboard,
  initializeIpErrorHandling,
  requireAdminAuth,
  handleAdminLogin
};

export default adminAuth; 